import type { Locale, TranslationKey } from './i18n';
import { formatNumber } from './format';

/**
 * The points wallet (CR-WALLET-001): what the API sends about it, and the small
 * rules the pages share. The server decides everything — the balance, the prices,
 * whether a reward can be redeemed; these only say it.
 */

export type RedemptionType =
  | 'REMOVE_QUARTER_DAY_PENALTY'
  | 'REMOVE_HALF_DAY_PENALTY'
  | 'REMOVE_FULL_DAY_PENALTY'
  | 'ADD_ONE_LEAVE_DAY'
  | 'CASH_REWARD';

export const REDEMPTION_TYPES: RedemptionType[] = [
  'REMOVE_QUARTER_DAY_PENALTY',
  'REMOVE_HALF_DAY_PENALTY',
  'REMOVE_FULL_DAY_PENALTY',
  'ADD_ONE_LEAVE_DAY',
  'CASH_REWARD',
];

export type PenaltyRedemption = Extract<RedemptionType, `REMOVE_${string}`>;

export const isPenaltyRedemption = (type: string): type is PenaltyRedemption => type.startsWith('REMOVE_');

export type UnavailableReason =
  | 'NOT_CONFIGURED'
  | 'NO_ELIGIBLE_PENALTY'
  | 'LEAVE_GRANT_UNAVAILABLE'
  | 'CASH_CURRENCY_UNSUPPORTED'
  | 'INSUFFICIENT_POINTS';

export interface HeldBack {
  awaitingDecision: number;
  dayNotOver: number;
  onPayroll: number;
}

export interface RedemptionOption {
  type: RedemptionType;
  /** One of it in points — a penalty, a day of leave, a cash bundle. Null while not priced. */
  points: number | null;
  priceVersion: string | null;
  available: boolean;
  reasons: UnavailableReason[];
  penaltyDays?: string;
  eligibleCount?: number;
  heldBack?: HeldBack;
  leaveType?: { id: string; code: string; name: string; nameAr: string | null } | null;
  year?: number;
  cash?: { pointsPerBundle: number; amountPerBundle: string; currency: string; maxBundles: number } | null;
  cashBlockedBecause?: 'OTHER_CURRENCY' | 'NO_SALARY' | null;
}

export interface WalletOptions {
  balance: number;
  options: RedemptionOption[];
}

export type PenaltyKind = 'SLA_VIEW' | 'SLA_ASSIGNMENT' | 'BREAK' | 'IDLE' | 'SHORT_DAY' | 'EARLY_END' | 'OTHER';

export interface EligiblePenalty {
  id: string;
  days: string;
  kind: PenaltyKind;
  /** YYYY-MM-DD, in the company's calendar. */
  date: string;
  detail: string | null;
  detailAr: string | null;
  effectiveMonth: string;
}

export interface Redemption {
  id: string;
  type: RedemptionType;
  status: 'COMPLETED' | 'PENDING_SETTLEMENT' | 'CANCELLED';
  pointsSpent: number;
  createdAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  penalty: { days: string | null; kind: PenaltyKind | null; date: string | null; detail: string | null; detailAr: string | null } | null;
  leave: { days: number; year: number; leaveType: { code: string; name: string; nameAr: string | null } } | null;
  cash: {
    bundles: number | null;
    amount: string;
    currency: string;
    status: 'PENDING' | 'PAID' | 'CANCELLED' | null;
    settledVia: 'PAYROLL' | 'MANUAL' | null;
    paidAt: string | null;
    payroll: { year: number; month: number } | null;
  } | null;
}

export interface WalletTransaction {
  id: string;
  kind: 'CREDIT' | 'REDEMPTION_DEBIT' | 'REFUND_CREDIT';
  deltaPoints: number;
  balanceAfter: number;
  occurredAt: string;
  redemption: Redemption | null;
}

export interface RedeemResult {
  replayed: boolean;
  balance: number;
  redemption: Redemption;
}

/** "1 point", "250 points", "0 نقطة" — Arabic says «نقطة» after a number, as the change request writes it. */
export function pointsText(points: number, locale: Locale, t: (key: TranslationKey) => string): string {
  const unit = Math.abs(points) === 1 ? t('wallet.pointUnit') : t('wallet.pointsUnit');
  return `${formatNumber(points, locale)} ${unit}`;
}

/** "+100 points" / "−50 points": a movement, its sign always written. */
export function deltaText(points: number, locale: Locale, t: (key: TranslationKey) => string): string {
  return `${points > 0 ? '+' : '−'}${pointsText(Math.abs(points), locale, t)}`;
}

/** The sentence a reward's first reason reads as, in the reader's language. */
export function reasonKey(option: RedemptionOption): TranslationKey | null {
  const reason = option.reasons[0];
  if (!reason) return null;
  if (reason === 'NO_ELIGIBLE_PENALTY') return `wallet.reason.NO_ELIGIBLE_PENALTY.${option.type}` as TranslationKey;
  if (reason === 'CASH_CURRENCY_UNSUPPORTED') {
    return option.cashBlockedBecause === 'NO_SALARY' ? 'wallet.reason.NO_SALARY' : 'wallet.reason.OTHER_CURRENCY';
  }
  return `wallet.reason.${reason}` as TranslationKey;
}

/** A refusal's code, as the sentence the redeem page shows. Nothing was spent by any of them. */
export function errorKey(code: string | undefined, details?: { reason?: string } | null): TranslationKey {
  switch (code) {
    case 'INSUFFICIENT_POINTS':
    case 'REWARD_NOT_CONFIGURED':
    case 'PENALTY_ALREADY_REVERSED':
    case 'PAYROLL_PERIOD_LOCKED':
    case 'LEAVE_GRANT_UNAVAILABLE':
    case 'PRICE_CHANGED':
    case 'IDEMPOTENCY_CONFLICT':
    case 'CONCURRENT_CHANGE':
      return `wallet.error.${code}` as TranslationKey;
    case 'NO_ELIGIBLE_PENALTY':
      return details?.reason === 'DAY_NOT_OVER'
        ? 'wallet.error.DAY_NOT_OVER'
        : details?.reason === 'NOT_CHARGED'
          ? 'wallet.error.NOT_CHARGED'
          : 'wallet.error.NO_ELIGIBLE_PENALTY';
    case 'CASH_CURRENCY_UNSUPPORTED':
      return details?.reason === 'NO_SALARY' ? 'wallet.reason.NO_SALARY' : 'wallet.reason.OTHER_CURRENCY';
    default:
      return 'wallet.error.general';
  }
}

/** A new key for each confirmation: sent twice — a double click, a retry — it is charged once. */
export function newIdempotencyKey(): string {
  // randomUUID needs a secure context (https, or localhost); a page served over plain http has getRandomValues only.
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const DATE_TAG: Record<Locale, string> = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' };

/** "20/10/2026" — DD/MM/YYYY, as the wallet's history writes a day (§3.2 B). */
export function dayOf(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(DATE_TAG[locale], { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value));
}

/** "14:05" — HH:mm on a 24-hour clock, in either language. */
export function timeOf(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(DATE_TAG[locale], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(value));
}

/** "12/10/2026", the day a penalty was for — a calendar date, never moved by the reader's timezone. */
export function calendarDate(date: string | null, locale: Locale): string {
  if (!date) return '—';
  const [y, m, d] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(DATE_TAG[locale], { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

/** A cash amount times a number of bundles, in cents, for the redeem page's preview — the server computes the real one. */
export function timesBundles(amountPerBundle: string, bundles: number): number {
  return (Math.round(Number(amountPerBundle) * 100) * bundles) / 100;
}
